import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ExternalLink, Shield, Zap, TreePine, Wallet, ArrowRight, Copy, CheckCircle2, Menu, X, FileText, Download, Globe2 } from 'lucide-react';

const CONTRACT = '0x76b7a4dfef135ddb9519c698768704a78b670e07';
const POLYGONSCAN = `https://polygonscan.com/token/${CONTRACT}`;

/* ─── i18n ─── */
const LangContext = createContext('pt');
const useLang = () => useContext(LangContext);

const T = {
  pt: {
    heroTitle1: 'O Valor da ',
    heroTitle2: 'Sobriedade',
    heroSub: 'A primeira economia regenerativa que recompensa sua saúde e recuperação pessoal através do protocolo ',
    heroProof: 'Proof of Recovery',
    connectWallet: 'Conectar VSA Wallet',
    viewPolygon: 'Ver no PolygonScan',
    networkBadge: 'Polygon Mainnet · ERC-20 · Chain ID 137',
    dashboardLabel: 'VSA Wallet · Saldo Total',
    supplyTotal: 'Supply Total',
    network: 'Rede',
    standard: 'Padrão',
    confirmations: 'Confirmações',
    blocks: '5 blocos',
    priceLabel: 'Preço AWA',
    comingSoon: 'Em breve',
    listingPending: 'Listagem pendente',
    proofTag: 'Proof of Recovery',
    howWorks: 'Como Funciona',
    howWorksSub: 'Cada passo na sua recuperação gera valor real no ecossistema AWARE.',
    step: 'Passo',
    step1Title: 'Check-in na U-Clean',
    step1Desc: 'Registre sua presença diária, complete check-ins emocionais e participe ativamente da comunidade de recuperação.',
    step2Title: 'Ganhe Recompensas AWA',
    step2Desc: 'Cada ação positiva gera tokens AWA. Sua consistência e compromisso com a sobriedade são recompensados com valor real.',
    step3Title: 'Evolua sua Árvore NFT',
    step3Desc: 'Sua Árvore de Sobriedade cresce com você — de semente a árvore sagrada. Um registro imutável da sua transformação.',
    utilityTag: 'Utilidade',
    utilityTitle1: 'AWA impulsiona o ',
    utilityTitle2: 'ecossistema U-Clean',
    util1Title: 'Recompensa Terapêutica', util1Desc: 'Check-ins, jornadas e participação ativa geram tokens AWA automaticamente.',
    util2Title: 'Economia Interna', util2Desc: 'Use AWA para acessar conteúdos exclusivos, cursos avançados e itens da loja.',
    util3Title: 'NFT de Sobriedade', util3Desc: 'Sua Árvore Viva evolui com seus tokens — um registro público da sua jornada.',
    util4Title: 'Governança Futura', util4Desc: 'Detentores de AWA participarão de decisões sobre o futuro da plataforma.',
    contractTitle: 'AWARE V1 (AWA)',
    contractSub: 'Token ERC-20 · Polygon Mainnet',
    contractVerified: 'Verificado publicamente. Sem taxas ocultas. Transparência total.',
    wpTag: 'Documentação',
    wpTitle: 'Whitepaper & Documentação Técnica',
    wpSub: 'Entenda a arquitetura do protocolo Proof of Recovery, a tokenomics do AWA e a visão de longo prazo do ecossistema.',
    wpBtn: 'Baixar Documentação Técnica',
    wpVersion: 'AWA Whitepaper v1.0 · PDF',
    roadmapTag: 'Roadmap',
    roadmapTitle: 'Evolução do Ecossistema',
    roadmapSub: 'Marcos estratégicos da jornada AWARE.',
    q1Title: 'Protocolo Proof of Recovery',
    q1Desc: 'Lançamento do mecanismo de recompensa baseado em check-ins verificáveis e atividades terapêuticas na plataforma U-Clean.',
    q2Title: 'VSA Wallet & NFTs U-Clean',
    q2Desc: 'Integração completa com carteira on-chain, vínculo por assinatura e Árvore de Sobriedade NFT com evolução por estágio.',
    q3Title: 'Listagem em Exchanges & Parcerias',
    q3Desc: 'Expansão para exchanges centralizadas (CEX), parcerias com centros de recuperação e programas governamentais.',
    ecoTag: 'Parceiros',
    ecoTitle: 'Ecossistema em Expansão',
    ecoSub: 'Parceiros estratégicos e integrações que fortalecem a rede AWARE.',
    ecoNote: 'Interessado em ser parceiro do ecossistema AWARE?',
    ecoContact: 'Entre em contato',
    ctaTitle1: 'Faça parte da ',
    ctaTitle2: 'revolução regenerativa',
    ctaSub: 'A sobriedade tem valor. O ecossistema AWARE transforma cada dia limpo em progresso verificável e recompensas reais na blockchain.',
    ctaUclean: 'Página Oficial U-Clean',
    footerCopy: 'AWARE Token © 2026 — O Ativo Digital da Recuperação',
    footerCompany: 'Visual Soul Tecnologia e Serviços Digitais Ltda',
  },
  en: {
    heroTitle1: 'The Value of ',
    heroTitle2: 'Sobriety',
    heroSub: 'The first regenerative economy that rewards your health and personal recovery through the ',
    heroProof: 'Proof of Recovery',
    connectWallet: 'Connect VSA Wallet',
    viewPolygon: 'View on PolygonScan',
    networkBadge: 'Polygon Mainnet · ERC-20 · Chain ID 137',
    dashboardLabel: 'VSA Wallet · Total Balance',
    supplyTotal: 'Total Supply',
    network: 'Network',
    standard: 'Standard',
    confirmations: 'Confirmations',
    blocks: '5 blocks',
    priceLabel: 'AWA Price',
    comingSoon: 'Coming soon',
    listingPending: 'Listing pending',
    proofTag: 'Proof of Recovery',
    howWorks: 'How It Works',
    howWorksSub: 'Every step in your recovery creates real value in the AWARE ecosystem.',
    step: 'Step',
    step1Title: 'Check-in on U-Clean',
    step1Desc: 'Log your daily presence, complete emotional check-ins and actively participate in the recovery community.',
    step2Title: 'Earn AWA Rewards',
    step2Desc: 'Every positive action generates AWA tokens. Your consistency and commitment to sobriety are rewarded with real value.',
    step3Title: 'Evolve your NFT Tree',
    step3Desc: 'Your Sobriety Tree grows with you — from seed to sacred tree. An immutable record of your transformation.',
    utilityTag: 'Utility',
    utilityTitle1: 'AWA powers the ',
    utilityTitle2: 'U-Clean ecosystem',
    util1Title: 'Therapeutic Reward', util1Desc: 'Check-ins, journeys and active participation automatically generate AWA tokens.',
    util2Title: 'Internal Economy', util2Desc: 'Use AWA to access exclusive content, advanced courses and store items.',
    util3Title: 'Sobriety NFT', util3Desc: 'Your Living Tree evolves with your tokens — a public record of your journey.',
    util4Title: 'Future Governance', util4Desc: 'AWA holders will participate in decisions about the platform\'s future.',
    contractTitle: 'AWARE V1 (AWA)',
    contractSub: 'ERC-20 Token · Polygon Mainnet',
    contractVerified: 'Publicly verified. No hidden fees. Full transparency.',
    wpTag: 'Documentation',
    wpTitle: 'Whitepaper & Technical Documentation',
    wpSub: 'Understand the Proof of Recovery protocol architecture, AWA tokenomics and the long-term ecosystem vision.',
    wpBtn: 'Download Technical Documentation',
    wpVersion: 'AWA Whitepaper v1.0 · PDF',
    roadmapTag: 'Roadmap',
    roadmapTitle: 'Ecosystem Evolution',
    roadmapSub: 'Strategic milestones of the AWARE journey.',
    q1Title: 'Proof of Recovery Protocol',
    q1Desc: 'Launch of the reward mechanism based on verifiable check-ins and therapeutic activities on the U-Clean platform.',
    q2Title: 'VSA Wallet & U-Clean NFTs',
    q2Desc: 'Full integration with on-chain wallet, signature linking and Sobriety Tree NFT with stage-based evolution.',
    q3Title: 'Exchange Listings & Partnerships',
    q3Desc: 'Expansion to centralized exchanges (CEX), partnerships with recovery centers and government programs.',
    ecoTag: 'Partners',
    ecoTitle: 'Expanding Ecosystem',
    ecoSub: 'Strategic partners and integrations that strengthen the AWARE network.',
    ecoNote: 'Interested in partnering with the AWARE ecosystem?',
    ecoContact: 'Get in touch',
    ctaTitle1: 'Join the ',
    ctaTitle2: 'regenerative revolution',
    ctaSub: 'Sobriety has value. The AWARE ecosystem transforms every clean day into verifiable progress and real rewards on the blockchain.',
    ctaUclean: 'U-Clean Official Page',
    footerCopy: 'AWARE Token © 2026 — The Digital Asset of Recovery',
    footerCompany: 'Visual Soul Tecnologia e Serviços Digitais Ltda',
  },
};

function useT(key) { return T[useContext(LangContext)]?.[key] || T.pt[key] || key; }

/* ─── Translation component ─── */
function Txt({ k }) {
  const lang = useContext(LangContext);
  return <>{T[lang]?.[k] || T.pt[k] || k}</>;
}

/* ─── Scroll Reveal ─── */
function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── Copy Contract Button ─── */
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={copy} className="ml-2 p-1.5 rounded-lg hover:bg-white/10 transition-colors" data-testid="copy-contract-btn">
      {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-zinc-500" />}
    </button>
  );
}

/* ─── Animated Counter ─── */
function CountUp({ target, duration = 2.5, className = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!isInView || hasStarted.current) return;
    hasStarted.current = true;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = (now - start) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isInView, target, duration]);

  const formatted = count.toLocaleString('pt-BR');

  return <span ref={ref} className={className}>{formatted}</span>;
}

/* ─── Animated Grid Background ─── */
function GridParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />
      {/* Animated glow orbs */}
      <motion.div animate={{ x: [0, 40, 0], y: [0, -30, 0], opacity: [0.06, 0.12, 0.06] }} transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-1/4 left-1/3 w-[500px] h-[500px] rounded-full bg-fuchsia-600 blur-[120px]" />
      <motion.div animate={{ x: [0, -30, 0], y: [0, 20, 0], opacity: [0.05, 0.1, 0.05] }} transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500 blur-[100px]" />
      <motion.div animate={{ x: [0, 20, 0], y: [0, 40, 0], opacity: [0.04, 0.09, 0.04] }} transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
        className="absolute top-1/3 right-1/3 w-[350px] h-[350px] rounded-full bg-amber-500 blur-[100px]" />
      {/* Floating particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            width: Math.random() * 2 + 1,
            height: Math.random() * 2 + 1,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -(Math.random() * 80 + 40), 0],
            opacity: [0, Math.random() * 0.3 + 0.05, 0],
          }}
          transition={{
            duration: Math.random() * 8 + 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: Math.random() * 5,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Price Card ─── */
function PriceCard() {
  const [price, setPrice] = useState(null);

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const res = await fetch(`https://api.coingecko.com/api/v3/simple/token_price/polygon-pos?contract_addresses=${CONTRACT}&vs_currencies=usd&include_24hr_change=true`);
        const data = await res.json();
        const info = data[CONTRACT.toLowerCase()];
        if (info) setPrice({ usd: info.usd, change: info.usd_24h_change });
      } catch { /* CoinGecko may not have this token listed yet */ }
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4" data-testid="price-card">
      <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Preco AWA</p>
      {price ? (
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-white">${price.usd.toFixed(6)}</span>
          <span className={`text-xs font-medium ${price.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {price.change >= 0 ? '+' : ''}{price.change?.toFixed(2)}%
          </span>
        </div>
      ) : (
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-zinc-400">Em breve</span>
          <span className="text-[10px] text-zinc-600">Listagem pendente</span>
        </div>
      )}
    </div>
  );
}

/* ─── Main Component ─── */
export default function AwareLandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState('pt');

  useEffect(() => {
    document.title = lang === 'pt'
      ? 'AWARE (AWA) — O Valor da Sobriedade | Ecossistema U-Clean'
      : 'AWARE (AWA) — The Value of Sobriety | U-Clean Ecosystem';
    const setMeta = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('og:title', 'AWARE (AWA) — O Valor da Sobriedade');
    setMeta('og:description', 'A primeira economia regenerativa que recompensa sobriedade. Token ERC-20 na Polygon Mainnet.');
    setMeta('og:image', 'https://ucleanapp.com.br/aware-color.png');
    setMeta('og:url', 'https://ucleanapp.com.br/aware');
    setMeta('og:type', 'website');
    return () => { document.title = 'U-Clean'; };
  }, [lang]);

  const navLinks = [
    { href: '#token', label: lang === 'pt' ? 'O Token' : 'Token' },
    { href: '#roadmap', label: 'Roadmap' },
    { href: '#contract', label: lang === 'pt' ? 'Contrato' : 'Contract' },
    { to: '/', label: 'U-Clean' },
  ];

  return (
    <LangContext.Provider value={lang}>
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }} data-testid="aware-landing">

      {/* ══════ HEADER ══════ */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 h-16">
          <a href="#hero" className="flex items-center gap-2.5">
            <img src="/aware-color.png" alt="AWA" className="w-8 h-8 rounded-full" />
            <span className="text-lg font-bold tracking-tight">AWARE</span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            {navLinks.map((l) =>
              l.to ? (
                <Link key={l.label} to={l.to} className="hover:text-white transition-colors">{l.label}</Link>
              ) : (
                <a key={l.label} href={l.href} className="hover:text-white transition-colors">{l.label}</a>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <button
              onClick={() => setLang(lang === 'pt' ? 'en' : 'pt')}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium border border-white/10 text-zinc-400 hover:text-white hover:border-white/20 transition-all"
              data-testid="lang-toggle"
            >
              <Globe2 className="h-3.5 w-3.5" />
              {lang === 'pt' ? 'EN' : 'PT'}
            </button>

            <Link to="/carteira" className="hidden sm:inline-flex px-5 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/20" data-testid="header-wallet-btn">
              <Txt k="connectWallet" />
            </Link>

            {/* Mobile hamburger */}
            <button className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5 overflow-hidden"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col gap-1 px-5 py-4">
                {navLinks.map((l) =>
                  l.to ? (
                    <Link key={l.label} to={l.to} onClick={() => setMobileMenuOpen(false)} className="py-2.5 text-sm text-zinc-400 hover:text-white transition-colors">{l.label}</Link>
                  ) : (
                    <a key={l.label} href={l.href} onClick={() => setMobileMenuOpen(false)} className="py-2.5 text-sm text-zinc-400 hover:text-white transition-colors">{l.label}</a>
                  )
                )}
                <Link to="/carteira" onClick={() => setMobileMenuOpen(false)} className="mt-2 flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-amber-500 to-amber-600 text-black">
                  <Wallet className="h-4 w-4" /> <Txt k="connectWallet" />
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ══════ HERO ══════ */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center pt-16">
        <GridParticles />

        <div className="relative z-10 max-w-4xl mx-auto px-5 text-center">
          <motion.div
            animate={{ scale: [1, 1.04, 1], opacity: [0.92, 1, 0.92] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            className="relative mx-auto mb-10 w-36 h-36 sm:w-48 sm:h-48"
          >
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-500/25 via-fuchsia-500/15 to-cyan-500/20 blur-3xl scale-125" />
            <img src="/aware-hero.png" alt="AWARE Token" className="relative w-full h-full rounded-full object-contain drop-shadow-2xl" data-testid="hero-logo" />
          </motion.div>

          <FadeUp>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6">
              <Txt k="heroTitle1" />
              <span className="bg-gradient-to-r from-amber-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent"><Txt k="heroTitle2" /></span>
            </h1>
          </FadeUp>

          <FadeUp delay={0.15}>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              <Txt k="heroSub" /><span className="text-zinc-200 font-medium"><Txt k="heroProof" /></span>.
            </p>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/carteira" className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/25 text-sm" data-testid="hero-wallet-btn">
                <Wallet className="h-4 w-4" /> <Txt k="connectWallet" />
              </Link>
              <a href={POLYGONSCAN} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white transition-all text-sm" data-testid="hero-polygonscan-btn">
                <Txt k="viewPolygon" /> <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </FadeUp>

          <FadeUp delay={0.45}>
            <div className="flex items-center justify-center gap-2 mt-10 text-xs text-zinc-500">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <Txt k="networkBadge" />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ══════ DASHBOARD PREVIEW ══════ */}
      <section className="py-24 px-5">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <div className="relative rounded-3xl border border-white/5 bg-gradient-to-br from-zinc-900/80 via-zinc-900/50 to-zinc-800/30 p-8 sm:p-12 backdrop-blur-xl overflow-hidden" data-testid="dashboard-preview">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-fuchsia-500/5 rounded-full blur-3xl" />

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-1">
                  <img src="/aware-pb.png" alt="" className="w-6 h-6 rounded-full opacity-60" />
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-widest">VSA Wallet · Saldo Total</p>
                </div>
                <div className="flex items-baseline gap-4 mb-6">
                  <CountUp
                    target={1000000000}
                    duration={2.5}
                    className="text-4xl sm:text-5xl font-black tracking-tight bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent"
                  />
                  <span className="text-xl text-amber-400/60 font-bold">AWA</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    { label: 'Supply Total', value: '10B' },
                    { label: 'Rede', value: 'Polygon' },
                    { label: 'Padrão', value: 'ERC-20' },
                    { label: 'Confirmações', value: '5 blocos' },
                  ].map((item) => (
                    <div key={item.label} className="rounded-xl bg-white/[0.02] border border-white/5 p-3">
                      <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{item.label}</p>
                      <p className="text-sm font-bold text-zinc-200 mt-0.5">{item.value}</p>
                    </div>
                  ))}
                  <PriceCard />
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ══════ PROOF OF RECOVERY ══════ */}
      <section id="ecosystem" className="py-24 px-5">
        <div className="max-w-5xl mx-auto">
          <FadeUp>
            <p className="text-xs font-semibold text-amber-400 uppercase tracking-widest text-center mb-3">Proof of Recovery</p>
            <h2 className="text-3xl sm:text-4xl font-black text-center mb-4 tracking-tight">Como Funciona</h2>
            <p className="text-zinc-500 text-center max-w-xl mx-auto mb-16 text-sm">Cada passo na sua recuperação gera valor real no ecossistema AWARE.</p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: '01',
                icon: Shield,
                color: 'from-cyan-500 to-cyan-600',
                glow: 'bg-cyan-500/10',
                title: 'Check-in na U-Clean',
                desc: 'Registre sua presença diária, complete check-ins emocionais e participe ativamente da comunidade de recuperação.',
              },
              {
                step: '02',
                icon: Zap,
                color: 'from-amber-500 to-amber-600',
                glow: 'bg-amber-500/10',
                title: 'Ganhe Recompensas AWA',
                desc: 'Cada ação positiva gera tokens AWA. Sua consistência e compromisso com a sobriedade são recompensados com valor real.',
              },
              {
                step: '03',
                icon: TreePine,
                color: 'from-fuchsia-500 to-fuchsia-600',
                glow: 'bg-fuchsia-500/10',
                title: 'Evolua sua Árvore NFT',
                desc: 'Sua Árvore de Sobriedade cresce com você — de semente a árvore sagrada. Um registro imutável da sua transformação.',
                useFenix: true,
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <FadeUp key={i} delay={i * 0.12}>
                  <div className="group relative rounded-2xl border border-white/5 bg-zinc-900/50 p-7 hover:border-white/10 transition-all duration-500 h-full" data-testid={`step-${item.step}`}>
                    <div className={`absolute top-0 right-0 w-32 h-32 ${item.glow} rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    <div className="relative z-10">
                      <span className="text-[10px] font-bold text-zinc-600 uppercase tracking-widest">Passo {item.step}</span>
                      {item.useFenix ? (
                        <div className="w-14 h-14 rounded-xl overflow-hidden mt-4 mb-5">
                          <img src="/aware-fenix.webp" alt="Fênix AWA" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mt-4 mb-5 shadow-lg`}>
                          <Icon className="h-6 w-6 text-white" strokeWidth={1.5} />
                        </div>
                      )}
                      <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                      <p className="text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════ TOKEN UTILITY ══════ */}
      <section id="token" className="py-24 px-5">
        <div className="max-w-5xl mx-auto">
          <FadeUp>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-xs font-semibold text-fuchsia-400 uppercase tracking-widest mb-3">Utilidade</p>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-6">
                  AWA impulsiona o{' '}
                  <span className="bg-gradient-to-r from-fuchsia-400 to-amber-400 bg-clip-text text-transparent">ecossistema U-Clean</span>
                </h2>
                <div className="space-y-5">
                  {[
                    { title: 'Recompensa Terapêutica', desc: 'Check-ins, jornadas e participação ativa geram tokens AWA automaticamente.' },
                    { title: 'Economia Interna', desc: 'Use AWA para acessar conteúdos exclusivos, cursos avançados e itens da loja.' },
                    { title: 'NFT de Sobriedade', desc: 'Sua Árvore Viva evolui com seus tokens — um registro público da sua jornada.' },
                    { title: 'Governança Futura', desc: 'Detentores de AWA participarão de decisões sobre o futuro da plataforma.' },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-500/20 to-fuchsia-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <ArrowRight className="h-3 w-3 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-0.5">{item.title}</p>
                        <p className="text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-center">
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-fuchsia-500/10 to-cyan-500/15 rounded-full blur-3xl scale-125" />
                  <img src="/aware-color.png" alt="AWARE" className="relative w-48 h-48 sm:w-60 sm:h-60 rounded-full drop-shadow-2xl" />
                </motion.div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ══════ CONTRACT ══════ */}
      <section id="contract" className="py-24 px-5">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <div className="rounded-3xl border border-white/5 bg-gradient-to-br from-zinc-900 to-zinc-900/50 p-8 sm:p-12 text-center" data-testid="contract-section">
              <div className="flex items-center justify-center gap-3 mb-6">
                <img src="/aware-color.png" alt="AWA" className="w-10 h-10 rounded-full" />
                <div className="text-left">
                  <p className="text-lg font-bold">AWARE V1 (AWA)</p>
                  <p className="text-xs text-zinc-500">Token ERC-20 · Polygon Mainnet</p>
                </div>
              </div>

              <div className="rounded-xl bg-black/40 border border-white/5 p-4 mb-6 flex items-center justify-center gap-2 flex-wrap">
                <span className="font-mono text-xs sm:text-sm text-zinc-300 break-all" data-testid="contract-address">{CONTRACT}</span>
                <CopyButton text={CONTRACT} />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
                <a href={POLYGONSCAN} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 rounded-full font-semibold bg-gradient-to-r from-violet-600 to-violet-700 text-white hover:from-violet-500 hover:to-violet-600 transition-all text-sm shadow-lg shadow-violet-500/20" data-testid="polygonscan-link">
                  <ExternalLink className="h-4 w-4" /> Ver no PolygonScan
                </a>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-xs text-zinc-400">
                  <svg className="w-4 h-4" viewBox="0 0 38 33" fill="none"><path d="M23.7 14.2l7-4V21l-7 4.2v-11zm-9.4-4l7 4v11l-7-4.2V10.2zm7 19.5l-7-4.2 7-4.2 7 4.2-7 4.2z" fill="#8247E5"/></svg>
                  Polygon Mainnet · Chain ID 137
                </div>
              </div>

              <p className="text-xs text-zinc-600">Verificado publicamente. Sem taxas ocultas. Transparência total.</p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ══════ WHITEPAPER ══════ */}
      <section id="whitepaper" className="py-24 px-5">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <div className="relative rounded-3xl border border-white/5 bg-gradient-to-br from-zinc-900 via-zinc-900/80 to-zinc-800/30 p-8 sm:p-12 overflow-hidden" data-testid="whitepaper-section">
              <div className="absolute top-0 left-0 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl" />
              <div className="relative z-10 flex flex-col sm:flex-row items-center gap-8">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500/10 to-fuchsia-500/10 border border-white/5 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-10 w-10 text-amber-400" strokeWidth={1.2} />
                </div>
                <div className="text-center sm:text-left flex-1">
                  <p className="text-xs font-semibold text-amber-400 uppercase tracking-widest mb-2"><Txt k="wpTag" /></p>
                  <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-3"><Txt k="wpTitle" /></h2>
                  <p className="text-sm text-zinc-500 leading-relaxed mb-6"><Txt k="wpSub" /></p>
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    <a href="#" className="flex items-center gap-2 px-6 py-3 rounded-full font-semibold bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500 transition-all text-sm shadow-lg shadow-amber-500/20" data-testid="whitepaper-download">
                      <Download className="h-4 w-4" /> <Txt k="wpBtn" />
                    </a>
                    <span className="text-[10px] text-zinc-600"><Txt k="wpVersion" /></span>
                  </div>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ══════ ROADMAP ══════ */}
      <section id="roadmap" className="py-24 px-5">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <p className="text-xs font-semibold text-cyan-400 uppercase tracking-widest text-center mb-3"><Txt k="roadmapTag" /></p>
            <h2 className="text-3xl sm:text-4xl font-black text-center mb-4 tracking-tight"><Txt k="roadmapTitle" /></h2>
            <p className="text-zinc-500 text-center max-w-lg mx-auto mb-16 text-sm"><Txt k="roadmapSub" /></p>
          </FadeUp>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-500/30 via-amber-500/30 to-fuchsia-500/30" />

            {[
              { q: 'Q1 2026', titleKey: 'q1Title', descKey: 'q1Desc', color: 'from-cyan-500 to-cyan-600', dotColor: 'bg-cyan-500', status: 'live', side: 'right' },
              { q: 'Q2 2026', titleKey: 'q2Title', descKey: 'q2Desc', color: 'from-amber-500 to-amber-600', dotColor: 'bg-amber-500', status: 'live', side: 'left' },
              { q: 'Q3 2026', titleKey: 'q3Title', descKey: 'q3Desc', color: 'from-fuchsia-500 to-fuchsia-600', dotColor: 'bg-fuchsia-500', status: 'upcoming', side: 'right' },
            ].map((item, i) => (
              <FadeUp key={i} delay={i * 0.15}>
                <div className={`relative flex items-start gap-6 mb-12 ${item.side === 'left' ? 'sm:flex-row-reverse' : ''}`}>
                  {/* Dot */}
                  <div className="absolute left-6 sm:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full ring-4 ring-[#0A0A0A] z-10" style={{ top: '8px' }}>
                    <div className={`w-full h-full rounded-full ${item.dotColor} ${item.status === 'live' ? 'animate-pulse' : 'opacity-40'}`} />
                  </div>

                  {/* Spacer for mobile */}
                  <div className="w-12 sm:hidden flex-shrink-0" />

                  {/* Card */}
                  <div className={`flex-1 ${item.side === 'left' ? 'sm:pr-16 sm:text-right' : 'sm:pl-16'}`}>
                    <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-6 hover:border-white/10 transition-all" data-testid={`roadmap-${item.q.replace(' ', '-')}`}>
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3 ${item.status === 'live' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>
                        {item.status === 'live' && <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                        {item.q}
                      </div>
                      <h3 className="text-lg font-bold mb-2"><Txt k={item.titleKey} /></h3>
                      <p className="text-sm text-zinc-500 leading-relaxed"><Txt k={item.descKey} /></p>
                    </div>
                  </div>

                  {/* Hidden spacer for desktop centering */}
                  <div className="hidden sm:block flex-1" />
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ ECOSYSTEM / PARTNERS ══════ */}
      <section id="partners" className="py-24 px-5">
        <div className="max-w-5xl mx-auto">
          <FadeUp>
            <p className="text-xs font-semibold text-fuchsia-400 uppercase tracking-widest text-center mb-3"><Txt k="ecoTag" /></p>
            <h2 className="text-3xl sm:text-4xl font-black text-center mb-4 tracking-tight"><Txt k="ecoTitle" /></h2>
            <p className="text-zinc-500 text-center max-w-lg mx-auto mb-12 text-sm"><Txt k="ecoSub" /></p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="partners-grid">
              {[
                { name: 'Polygon', icon: <svg className="w-7 h-7" viewBox="0 0 38 33" fill="none"><path d="M23.7 14.2l7-4V21l-7 4.2v-11zm-9.4-4l7 4v11l-7-4.2V10.2zm7 19.5l-7-4.2 7-4.2 7 4.2-7 4.2z" fill="#8247E5"/></svg> },
                { name: 'U-Clean', icon: <img src="/aware-color.png" alt="" className="w-7 h-7 rounded-full" /> },
                { name: 'VSA Wallet', icon: <Wallet className="w-6 h-6 text-amber-400" /> },
                { name: 'PolygonScan', icon: <ExternalLink className="w-6 h-6 text-violet-400" /> },
                { name: 'CoinGecko', icon: <span className="text-lg">🦎</span>, placeholder: true },
                { name: 'DEX', icon: <span className="text-lg font-bold text-fuchsia-400">DEX</span>, placeholder: true },
                { name: 'CEX', icon: <span className="text-lg font-bold text-cyan-400">CEX</span>, placeholder: true },
                { name: 'Recovery Centers', icon: <Shield className="w-6 h-6 text-emerald-400" />, placeholder: true },
              ].map((p, i) => (
                <div key={i} className={`rounded-2xl border p-5 flex flex-col items-center gap-3 text-center transition-all ${p.placeholder ? 'border-dashed border-white/5 bg-transparent opacity-50' : 'border-white/5 bg-zinc-900/30 hover:border-white/10'}`}>
                  <div className="w-12 h-12 rounded-xl bg-white/[0.03] flex items-center justify-center">
                    {p.icon}
                  </div>
                  <span className="text-xs font-medium text-zinc-400">{p.name}</span>
                  {p.placeholder && <span className="text-[9px] text-zinc-600 -mt-1">Em breve</span>}
                </div>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.2}>
            <div className="text-center mt-10">
              <p className="text-xs text-zinc-600 mb-2"><Txt k="ecoNote" /></p>
              <a href="mailto:contato.celso@visualsoul.com" className="text-xs text-amber-400 hover:underline"><Txt k="ecoContact" /></a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ══════ CTA FINAL ══════ */}
      <section className="py-24 px-5">
        <div className="max-w-3xl mx-auto text-center">
          <FadeUp>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-5">
              <Txt k="ctaTitle1" />
              <span className="bg-gradient-to-r from-amber-400 to-fuchsia-400 bg-clip-text text-transparent"><Txt k="ctaTitle2" /></span>
            </h2>
            <p className="text-zinc-500 max-w-xl mx-auto mb-10 text-sm leading-relaxed">
              <Txt k="ctaSub" />
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/carteira" className="flex items-center gap-2 px-8 py-4 rounded-full font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500 transition-all shadow-xl shadow-amber-500/25 text-sm" data-testid="cta-wallet-btn">
                <Wallet className="h-4 w-4" /> <Txt k="connectWallet" />
              </Link>
              <Link to="/" className="flex items-center gap-2 px-8 py-4 rounded-full font-semibold border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white transition-all text-sm" data-testid="cta-uclean-btn">
                <Txt k="ctaUclean" /> <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ══════ FOOTER ══════ */}
      <footer className="border-t border-white/5 py-10 px-5">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/aware-color.png" alt="AWA" className="w-7 h-7 rounded-full" />
              <span className="text-sm font-bold text-zinc-400">AWARE Token</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-zinc-600">
              <Link to="/" className="hover:text-zinc-400 transition-colors">ucleanapp.com.br</Link>
              <a href={POLYGONSCAN} target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">PolygonScan</a>
              <a href="mailto:contato.celso@visualsoul.com" className="hover:text-zinc-400 transition-colors">Contato</a>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-[10px] text-zinc-700"><Txt k="footerCopy" /></p>
            <p className="text-[9px] text-zinc-800 mt-1"><Txt k="footerCompany" /></p>
          </div>
        </div>
      </footer>
    </div>
    </LangContext.Provider>
  );
}
